import appConstants from '../../../../../../appConstants';
import { formatArriveAtTerminalTime } from '../../../utils/utils';
import { getMinutesInHourAndMinutesStr } from '../../../../Reports/DriverPerformance/util';
import { getMinutesfromTimeString } from '@dataGrid/gridUtilities';
import { createGroupedData } from '../../Pickups/GroupPickup/utils';

const getFormattedKPIRecommendationData = (recommendation, routesList) => {
  const { estimatedTrailerSpaceRemaining, estimatedTimeOfArrival, ...restRecommendationDetails } =
    recommendation;
  const matchedRoute = routesList.find(
    (route) => route.routeId.toLowerCase() === recommendation.routeId?.toLowerCase()
  );
  if (matchedRoute) {
    const { color, driverName, driverNumber, currentStatus, stops } = matchedRoute;
    return {
      //driver route properties
      color,
      driverName,
      driverNumber,
      currentStatus,
      routeStops: getStopsPropertiesForRecommendation(stops),

      //recommendation properties
      trailerCapacity: estimatedTrailerSpaceRemaining,
      arrivalTime: estimatedTimeOfArrival,
      ...restRecommendationDetails
    };
  }
  return recommendation;
};

const getStopsPropertiesForRecommendation = (stops, color = undefined) => {
  return stops.map(({ stopSequence, isStopCompleted, name, stopId }) => {
    return {
      stopSequence,
      isStopCompleted,
      name,
      stopId,
      color
    };
  });
};

const getDriverAssignmentsList = ({ pickupsList, currentActiveRoutes, driverAssignments, isPickupOptimizationFlow = false }) => {
  return driverAssignments.map((driverAssignment) => {
    const { routeStops, ...driverAssignmentDetails } = driverAssignment;
    if (driverAssignmentDetails.driverId) {
      const recommendedDriver = findDriverRoute(
        currentActiveRoutes,
        driverAssignmentDetails.driverId
      );
      const stops = getStopInfo({
        routeStops,
        pickupsList,
        currentActiveRoutes,
        recommendedDriver,
        isPickupOptimizationFlow,
      }).filter(stop => stop !== null);
      return {
        ...recommendedDriver, // new Driver
        ...driverAssignmentDetails,
        stops // new stopList including virtual stops
      };
    } else {
      //when pickup is unassign we will show pickup data from store
      const unassignPickupData = getUnassignPickupInfo({
        driverAssignment: driverAssignmentDetails,
        pickupsList,
        currentActiveRoutes,
        isPickupOptimizationFlow
      })
      if (!unassignPickupData.length) {
        return null;
      }
      return {
        isUnassignedPickup: true,
        ...driverAssignmentDetails,
        ...unassignPickupData[0]
      };
    }
  }).filter(driverAssignment => Boolean(driverAssignment));
};

const getUnassignPickupInfo = ({ driverAssignment, pickupsList, currentActiveRoutes, isPickupOptimizationFlow = false }) => {
  const lookupField = getPickupLookupField(isPickupOptimizationFlow);
  const lookupIds = isPickupOptimizationFlow ? driverAssignment.groupIds : driverAssignment.pickupIds;
  return lookupIds
    .map((id) => {
      return getPickupFromList({ key: lookupField, pickupList: pickupsList, id: id });
    })
    .map((pickup) => {
      if (!pickup) {
        return null;
      }
      const previousDriver = pickup.driverId
        ? findDriverRoute(currentActiveRoutes, pickup.driverId)
        : null; // for already assign pickup search assigned driver and show it as a previous driver
      return {
        ...pickup,
        driverId: null,
        previousDriver
      };
    }).filter(pickup => Boolean(pickup));
};

const findDriverRoute = (routesList, driverId) => {
  return routesList.find((route) => route.driverId.toLowerCase() === driverId?.toLowerCase());
};

const isSameDriverAssignment = (previousDriverId, driverId) => {
  return (
    Boolean(previousDriverId) &&
    Boolean(driverId) &&
    previousDriverId.toLowerCase() === driverId.toLowerCase()
  );
};

// Pickup already on the recommended driver's route only has route-stop fields (name, readyTime); merge pickup-list data so PickupInfo gets shipperName and pickup window.
const mergeRecommendedDriverStop = ({
  recommendedDriverStop,
  stopData,
  routeStop,
  routeInfo,
  recommendedDriver,
}) => {
  const mergedStop = stopData
    ? { ...recommendedDriverStop, ...stopData }
    : recommendedDriverStop;
  const keepExistingAssignment = isSameDriverAssignment(
    routeStop.previousDriverId,
    recommendedDriver?.driverId
  );

  return {
    ...mergedStop,
    ...routeStop,
    arrivalTime: routeStop.estimatedTimeOfArrival,
    departureTime: routeStop.estimatedTimeOfDeparture,
    ...routeInfo,
    ...(stopData
      ? {
          previousDriver: keepExistingAssignment ? null : stopData.previousDriver,
        }
      : {}),
  };
};

const getStopInfo = ({ routeStops, pickupsList, currentActiveRoutes, recommendedDriver, isPickupOptimizationFlow = false }) => {
  const routeInfo = getRouteInfo(recommendedDriver);
  const pickupParams = { pickupsList, currentActiveRoutes, isPickupOptimizationFlow };

  return routeStops.map((routeStop) => {
    const params = { ...pickupParams, routeStop };
    const recommendedDriverStop = recommendedDriver?.stops?.find(
      (stop) =>
        stop.stopId?.toLowerCase() === routeStop.stopId?.toLowerCase() ||
        (stop.groupId && stop.groupId?.toLowerCase() === routeStop.groupId?.toLowerCase())
    );
    if (recommendedDriverStop) {
      const stopData = getPickupFormattedData(params);
      return mergeRecommendedDriverStop({
        recommendedDriverStop,
        stopData,
        routeStop,
        routeInfo,
        recommendedDriver,
      });
    }
    const stopData = getPickupFormattedData(params);
    if (routeStop.isVirtualStop) {
      return stopData ? { ...stopData, ...routeInfo } : null;
    }
    if (routeStop.groupId) {
      return stopData
        ? {
          ...stopData,
          ...routeStop,
          arrivalTime: routeStop.estimatedTimeOfArrival,
          departureTime: routeStop.estimatedTimeOfDeparture,
          ...routeInfo,
        }
        : null;
    }
    return null;
  });
};

const getPickupLookupField = (isPickupOptimizationFlow) => {
  return isPickupOptimizationFlow ? 'groupId' : 'pickupId';
};

const getPickupFromList = ({ key, pickupList, id }) => {
  return pickupList.find((pickup) => pickup[key]?.toLowerCase() === id?.toLowerCase());
}

const getPickupData = ({ routeStop, pickupsList, isPickupOptimizationFlow = false }) => {
  //if stop is pickup stop, get pickup data from pickup list using groupId if pickup optimization flow is true else using pickupId
  const pickupData = getPickupFromList({ key: getPickupLookupField(isPickupOptimizationFlow), pickupList: pickupsList, id: routeStop[getPickupLookupField(isPickupOptimizationFlow)] });
  if (pickupData) {
    //if data is present for groupId, return pickup data
    return pickupData;
  }
  if (routeStop.pickupIdList?.length) {
    //if data is not present for groupId, get pickup data from pickup list using pickupIdList
    const pickupDataList = routeStop.pickupIdList.map(pickupId => {
      return getPickupFromList({ key: "pickupId", pickupList: pickupsList, id: pickupId });
    }).filter(pickup => Boolean(pickup)) ?? [];
    return pickupDataList?.length > 1 ? createGroupedData(pickupDataList)?.[0] : pickupDataList?.length ? pickupDataList[0] : null;
  }
  return null;
}

const getPickupFormattedData = ({ pickupsList, routeStop, currentActiveRoutes, isPickupOptimizationFlow = false }) => {
  const previousDriver = routeStop.previousDriverId
    ? findDriverRoute(currentActiveRoutes, routeStop.previousDriverId)
    : null;
  const pickupData = getPickupData({ routeStop, pickupsList, isPickupOptimizationFlow });
  return pickupData
    ? {
      ...routeStop,
      ...pickupData,
      latitude: pickupData.shipperLatitude,
      longitude: pickupData.shipperLongitude,
      stopType: appConstants.STOP_TYPE.Pickup,
      name: pickupData.shipperName,
      address: pickupData.shipperAddress,
      readyTime: pickupData.pickupReadyTime,
      closeTime: pickupData.pickupCloseTime,
      arrivalTime: routeStop.estimatedTimeOfArrival,
      departureTime: routeStop.estimatedTimeOfDeparture,
      previousDriver: previousDriver,
      address2: pickupData.address2,
      city: pickupData.shipperCity,
      state: pickupData.shipperState,
      zip: pickupData.shipperZip
    }
    : null;
};

const getRouteInfo = (recommendedDriver) => {
  return {
    driverId: recommendedDriver?.driverId,
    driverName: recommendedDriver?.driverName,
    driverNumber: recommendedDriver?.driverNumber,
    color: recommendedDriver?.color
  };
};

//----- recommendation kpi utils start -----
const getEstimatedMilesRemaining = ({ estimatedMilesRemaining, estimatedIncreaseInMiles = 0 }) => {
  let increaseInMiles = Math.round(estimatedIncreaseInMiles);
  const sign = increaseInMiles > 0 ? '+' : '-';
  if (increaseInMiles) {
    increaseInMiles = `(${sign}${Math.abs(increaseInMiles)})`;
  }
  return `${Math.round(estimatedMilesRemaining)} ${increaseInMiles || ''}`;
};

const getTrailerCapacityText = (data) => {
  return `${parseFloat(Number(data.trailerCapacity ?? 0)?.toFixed(2))} Pallets left`
}

const getEstimatedTravelTime = ({
  estimatedTravelTimeRemaining,
  estimatedIncreaseInTravelTime
}) => {
  const sign = estimatedIncreaseInTravelTime > 0 ? '+' : '-';
  if (estimatedIncreaseInTravelTime) {
    estimatedIncreaseInTravelTime = `(${sign}${getMinutesInHourAndMinutesStr(
      Math.abs(estimatedIncreaseInTravelTime),
      true
    )})`;
  }
  return `${getMinutesInHourAndMinutesStr(estimatedTravelTimeRemaining, true)} ${estimatedIncreaseInTravelTime || ''
    }`;
};

const getArriveAtTerminalTime = (arriveAtTerminalTime) => {
  return formatArriveAtTerminalTime(arriveAtTerminalTime);
};

const formatRecommendedArriveAtTerminalTime = (
  currentArriveAtTerminalTime,
  recommendedArrivalAtTerminalTime
) => {
  const formattedRecommendedTime = formatArriveAtTerminalTime(recommendedArrivalAtTerminalTime);
  const currentArrivalTimeInMin = getMinutesfromTimeString(
    formatArriveAtTerminalTime(currentArriveAtTerminalTime)
  );
  const recommendedArrivalTimeInMin = getMinutesfromTimeString(formattedRecommendedTime);
  const timeDiff = recommendedArrivalTimeInMin - currentArrivalTimeInMin;
  const sign = timeDiff > 0 ? '+' : '-';
  let estimatedIncreaseInArrivalTime = '';
  if (timeDiff) {
    estimatedIncreaseInArrivalTime = `(${sign}${getMinutesInHourAndMinutesStr(
      Math.abs(timeDiff),
      true
    )})`;
  }
  return `${formattedRecommendedTime} ${estimatedIncreaseInArrivalTime || ''}`;
};
//----- recommendation kpi utils end -----

export {
  getFormattedKPIRecommendationData,
  getStopsPropertiesForRecommendation,
  getDriverAssignmentsList,
  findDriverRoute,
  isSameDriverAssignment,
  mergeRecommendedDriverStop,
  getEstimatedMilesRemaining,
  getEstimatedTravelTime,
  getArriveAtTerminalTime,
  formatRecommendedArriveAtTerminalTime,
  getTrailerCapacityText
};
